import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip,
  useTheme
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { 
  copyMeetingLink, 
  createGoogleCalendarUrl, 
  openMeetingLink,
  canShowMeetLink,
  formatDateTime 
} from './GoogleMeetUtils';
import { toast } from 'react-toastify';

interface GoogleMeetCardProps {
  session: any;
  onJoinMeeting?: () => void;
}

const GoogleMeetCard: React.FC<GoogleMeetCardProps> = ({ 
  session, 
  onJoinMeeting 
}) => {
  const theme = useTheme();
  
  const handleCopyMeetingLink = async () => {
    if (session?.meetingLink) {
      const result = await copyMeetingLink(session.meetingLink);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    }
  };

  const handleJoinMeeting = () => {
    if (session?.meetingLink) {
      openMeetingLink(session.meetingLink);
      if (onJoinMeeting) {
        onJoinMeeting();
      }
    }
  };

  const handleAddToCalendar = () => {
    const calendarUrl = createGoogleCalendarUrl(session);
    if (calendarUrl) {
      window.open(calendarUrl, '_blank');
    } else {
      toast.error('Unable to create calendar event');
    }
  };

  // Don't show the card if meeting link is not available or session status doesn't allow it
  if (!canShowMeetLink(session)) {
    return null;
  }

  const sessionDateTime = session?.date && session?.sessionTimeslots?.[0]?.timeslot?.startAt 
    ? `${session.date}T${session.sessionTimeslots[0].timeslot.startAt}:00`
    : null;

  return (
    <Card sx={{ 
      mb: 3, 
      backgroundColor: theme.colors.alpha.white[100],
      border: `1px solid ${theme.colors.alpha.black[10]}`,
      borderRadius: theme.general.borderRadius,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
        transform: 'translateY(-1px)'
      }
    }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <VideocamIcon sx={{ 
            mr: 2.5, 
            fontSize: '1.75rem',
            color: '#D58E4E',
            filter: 'drop-shadow(0 1px 2px rgba(213, 142, 78, 0.3))'
          }} />
          <Typography variant="h6" sx={{ 
            fontWeight: '600', 
            flexGrow: 1,
            color: theme.colors.secondary.main,
            fontSize: '19px',
            letterSpacing: '-0.01em'
          }}>
            Video Consultation
          </Typography>
          <Chip 
            label="Confirmed" 
            sx={{ 
              backgroundColor: '#D58E4E',
              color: theme.colors.alpha.white[100],
              fontWeight: '600',
              fontSize: '12px',
              height: '32px',
              px: 1.5,
              boxShadow: '0 2px 4px rgba(213, 142, 78, 0.3)',
              '& .MuiChip-label': {
                px: 1
              }
            }} 
            size="small"
          />
        </Box>
        
        {/* Session Details */}
        <Box sx={{ 
          backgroundColor: theme.colors.alpha.black[5], 
          borderRadius: theme.general.borderRadiusSm,
          p: 2.5,
          mb: 3,
          border: `1px solid ${theme.colors.alpha.black[5]}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ 
              width: 6, 
              height: 6, 
              borderRadius: '50%', 
              backgroundColor: theme.colors.info.main,
              mr: 2,
              boxShadow: `0 0 0 2px ${theme.colors.info.lighter}`
            }} />
            <Typography variant="body2" sx={{ 
              color: theme.colors.secondary.main,
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: 1.4
            }}>
              Session Date: {sessionDateTime ? formatDateTime(sessionDateTime) : 'Aug 12, 2025, 05:30 AM'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ 
              width: 6, 
              height: 6, 
              borderRadius: '50%', 
              backgroundColor: '#D58E4E',
              mr: 2,
              boxShadow: '0 0 0 2px rgba(213, 142, 78, 0.2)'
            }} />
            <Typography variant="body2" sx={{ 
              color: theme.colors.secondary.main,
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: 1.4
            }}>
              Clinician: {session.clinician?.user?.fullName || 'Asmit'}
            </Typography>
          </Box>
        </Box>
        
        {/* Meeting Link */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          p: 2.5,
          mb: 3.5,
          backgroundColor: theme.colors.alpha.black[5],
          borderRadius: theme.general.borderRadiusSm,
          border: `1px solid ${theme.colors.alpha.black[10]}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: theme.colors.alpha.black[5],
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
          }
        }}>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            backgroundColor: theme.colors.alpha.black[30],
            mr: 2.5,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
          }} />
          <Typography variant="body2" sx={{ 
            color: theme.colors.alpha.black[70],
            fontWeight: '500',
            wordBreak: 'break-all',
            fontSize: '13px',
            lineHeight: 1.5,
            fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace'
          }}>
            Meeting Link: {session.meetingLink || 'https://meet.google.com/sjo-wmra-qdq'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<VideocamIcon sx={{ fontSize: '1.1rem' }} />}
            onClick={handleJoinMeeting}
            sx={{
              backgroundColor: '#D58E4E',
              color: theme.colors.alpha.white[100],
              fontWeight: '600',
              textTransform: 'none',
              borderRadius: theme.general.borderRadiusSm,
              px: 3.5,
              py: 1.25,
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(213, 142, 78, 0.3), 0 1px 3px rgba(213, 142, 78, 0.2)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#c17d42',
                boxShadow: '0 4px 12px rgba(213, 142, 78, 0.4), 0 2px 6px rgba(213, 142, 78, 0.3)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: '0 1px 4px rgba(213, 142, 78, 0.3)',
              }
            }}
          >
            Join Meeting
          </Button>

          <Button
            variant="outlined"
            onClick={handleCopyMeetingLink}
            sx={{
              borderColor: theme.colors.alpha.black[30],
              color: '#D58E4E',
              fontWeight: '500',
              textTransform: 'none',
              borderRadius: theme.general.borderRadiusSm,
              p: 1.25,
              minWidth: '44px',
              height: '44px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#D58E4E',
                backgroundColor: 'rgba(213, 142, 78, 0.06)',
                boxShadow: '0 2px 6px rgba(213, 142, 78, 0.15)',
                transform: 'translateY(-1px)',
              }
            }}
          >
            <ContentCopyIcon sx={{ fontSize: '1.1rem' }} />
          </Button>

          <Button
            variant="outlined"
            onClick={handleAddToCalendar}
            sx={{
              borderColor: theme.colors.alpha.black[30],
              color: '#D58E4E',
              fontWeight: '500',
              textTransform: 'none',
              borderRadius: theme.general.borderRadiusSm,
              p: 1.25,
              minWidth: '44px',
              height: '44px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#D58E4E',
                backgroundColor: 'rgba(213, 142, 78, 0.06)',
                boxShadow: '0 2px 6px rgba(213, 142, 78, 0.15)',
                transform: 'translateY(-1px)',
              }
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: '1.1rem' }} />
          </Button>

          <Button
            variant="outlined"
            onClick={() => window.open(session.meetingLink, '_blank')}
            sx={{
              borderColor: theme.colors.alpha.black[30],
              color: '#D58E4E',
              fontWeight: '500',
              textTransform: 'none',
              borderRadius: theme.general.borderRadiusSm,
              p: 1.25,
              minWidth: '44px',
              height: '44px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#D58E4E',
                backgroundColor: 'rgba(213, 142, 78, 0.06)',
                boxShadow: '0 2px 6px rgba(213, 142, 78, 0.15)',
                transform: 'translateY(-1px)',
              }
            }}
          >
            <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GoogleMeetCard;
